// BETA 13.71 — Weapon Visual Test Lab
// Standalone testing screen launched from the start menu. Wraps GameCanvas
// in lab mode (frozen invincible player, all items unlocked, no run-end)
// and overlays a right-side control panel for live weapon/item/enemy testing
// + time-stamped notes that capture the active loadout, exported as CSV.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import GameCanvas from './GameCanvas';
import BarrelCalibrator from './BarrelCalibrator';
import StatsAndVFXLab, { STATS_LAB_PANEL_WIDTH } from './StatsAndVFXLab';
import SoundInspector from './SoundInspector';
import type { CharacterDef } from '../game/assets';
import { CHARACTERS } from '../game/assets';
import type { AudioSettings } from '../game/types';
import {
  getEquippedWeaponIds,
  setWeaponEquipped,
  listAllWeapons,
  getEquippedItems,
  setItemStacks,
  listAllItems,
  listAllEnemyTypes,
  spawnEnemiesAround,
  clearAllEnemies,
  getAliveEnemyCounts,
  saveLabNote,
  downloadLabCsv,
  clearLabNotes,
  loadLabNotes,
  newLabSessionId,
  // BETA 14.05 — skip level-ups toggle (persisted via localStorage)
  isLabSkipLevelUp,
  setLabSkipLevelUp,
  // BETA 14.09 — perc stacking + time lock
  listAllPercsWithState,
  incrementPercLevel,
  clearPercFromSlot,
  getLabFrozenTimeSec,
  setLabFrozenTimeSec,
  // BETA 14.15 — randomizer + presets + multi-build session CSV
  randomizeBuild,
  // BETA 17.84 — LUDICROUS build variant (all items / shrines / percs maxed)
  randomizeLudicrousBuild,
  randomizeStressBuild,
  applyBuild,
  getSessionRowCount,
  downloadMultiBuildCsv,
  clearSession,
  PRESET_BUILDS,
  type PresetBuild,
  // BETA 14.18 — wipe loadout without resetting session rows
  clearLoadout,
  // BETA 14.38 — Retreat simulation panel
  getRetreatTuning,
  setRetreatTuning,
  resetRetreatTuning,
  spawnEnemiesInArc,
  getLocalRetreatEvents,
  // BETA 14.39 — zoom + FOV box overlay
  getRetreatSimZoom,
  setRetreatSimZoom,
  getRetreatSimShowFovBox,
  setRetreatSimShowFovBox,
  // BETA 14.42 — wave spawn rate multiplier (VS-style continuous density)
  getWaveSpawnRateMult,
  setWaveSpawnRateMult,
  // BETA 20.48 — ultra item toggles (separate ultraItems boss-chest subsystem)
  listAllUltraItems,
  getEquippedUltraItemIds,
  setUltraItemEquipped,
} from '../game/weaponLab';
import { toggleBot, isBotEnabled } from '../game/autoPlaytester';
// BETA 17.96 — VFX master toggle.
import { isVfxEnabled, setVfxEnabled } from '../game/debugState';

interface Props {
  onBack: () => void;
  audioSettings?: AudioSettings;
  username?: string;
}

const SIDEBAR_W = 360;

export default function WeaponLabScreen({ onBack, audioSettings, username }: Props) {
  const [character, setCharacter] = useState<CharacterDef>(CHARACTERS[0]);
  const sessionIdRef = useRef<string>(newLabSessionId());

  // Force re-render every 250ms so the live panel stats refresh.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 250);
    return () => clearInterval(id);
  }, []);

  // Notes state
  const [noteText, setNoteText] = useState('');
  const [noteHistory, setNoteHistory] = useState(() => loadLabNotes());

  // BETA 14.06 — Barrel Calibrator modal state
  const [calibratorOpen, setCalibratorOpen] = useState(false);
  // BETA 14.75 — Stats & VFX Lab modal state
  const [statsLabOpen, setStatsLabOpen] = useState(false);
  // BETA 15.64 — Sound Inspector modal state
  const [soundInspectorOpen, setSoundInspectorOpen] = useState(false);

  // Spawn controls
  const [spawnEnemyId, setSpawnEnemyId] = useState<string>('zombie');
  const [spawnCount, setSpawnCount] = useState<number>(10);
  // BETA 20.48 — force a re-render after an ultra-item toggle so its checkbox
  // reflects the new live state immediately (toggles mutate engine state).
  const [, bumpUltraTick] = useState(0);

  const allWeapons = useMemo(() => listAllWeapons(), []);
  const allItems = useMemo(() => listAllItems(), []);
  const allUltras = useMemo(() => listAllUltraItems(), []);
  const allEnemies = useMemo(() => listAllEnemyTypes(), []);

  const equippedWeaponIds = getEquippedWeaponIds();
  const equippedItems = getEquippedItems();
  const equippedUltraIds = getEquippedUltraItemIds();
  const aliveEnemies = getAliveEnemyCounts();

  // ─── Helpers ────────────────────────────────────────────────────────────────
  function handleSaveNote() {
    if (!noteText.trim()) return;
    saveLabNote({
      sessionId: sessionIdRef.current,
      character: character.name,
      notes: noteText.trim(),
    });
    setNoteText('');
    setNoteHistory(loadLabNotes());
  }
  function handleClearNotes() {
    if (!window.confirm('Erase all saved lab notes? This cannot be undone.')) return;
    clearLabNotes();
    setNoteHistory([]);
  }
  function handleSpawn() {
    // Spawn around screen center (player is at world center too — see GameCanvas).
    spawnEnemiesAround(spawnEnemyId, spawnCount, 0, 0, 350);
  }

  // ─── Styles ─────────────────────────────────────────────────────────────────
  const panelStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0, right: 0,
    width: SIDEBAR_W,
    height: '100vh',
    overflowY: 'auto',
    background: 'rgba(8, 10, 14, 0.92)',
    borderLeft: '1px solid rgba(80, 200, 255, 0.25)',
    color: '#cfd8e3',
    fontFamily: '"Share Tech Mono", monospace',
    fontSize: 12,
    padding: '14px 16px 24px',
    boxSizing: 'border-box',
    zIndex: 50,
  };
  const sectionStyle: React.CSSProperties = { marginBottom: 14 };
  const h2: React.CSSProperties = {
    fontSize: 11, letterSpacing: '0.18em', color: '#7fdcff',
    textTransform: 'uppercase', borderBottom: '1px solid rgba(80,200,255,0.18)',
    paddingBottom: 4, marginBottom: 8,
  };
  const btn: React.CSSProperties = {
    background: 'rgba(40,80,120,0.5)', color: '#e6f2ff',
    border: '1px solid rgba(80,200,255,0.4)', padding: '6px 10px',
    cursor: 'pointer', fontSize: 11, letterSpacing: '0.06em',
    fontFamily: 'inherit', borderRadius: 2,
  };
  const btnRed: React.CSSProperties = { ...btn, borderColor: '#ff5544', color: '#ffb0a4' };
  const row: React.CSSProperties = { display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 };

  return (
    <>
      {/* Game canvas — labMode keeps player frozen + invincible.
          BETA 15.65 — When Combat Test Bench is open, leave room on the LEFT
          for its side panel so the real game (with the user's character +
          real weapons firing real bullets) is visible alongside the sliders. */}
      <div style={{
        position: 'fixed', inset: 0,
        paddingRight: SIDEBAR_W,
        paddingLeft: statsLabOpen ? STATS_LAB_PANEL_WIDTH : 0,
        transition: 'padding-left 120ms ease',
      }}>
        <GameCanvas
          character={character}
          onBack={onBack}
          audioSettings={audioSettings}
          username={username}
          labMode={true}
        />
      </div>

      {/* Right sidebar */}
      <div style={panelStyle}>
        <div style={{ ...sectionStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 14, color: '#7fdcff', letterSpacing: '0.12em' }}>WEAPON LAB</div>
          <button
            style={btn}
            onClick={() => {
              // BETA 18.3 — Dylan: "i am playing the game regular and a few
              // seconds in it auto populated me weapons and perks and items?
              // why. I never asked you to have them in the regular game."
              // Lab state was leaking into regular runs because exiting via
              // ← MENU just navigated home without clearing equipped gear
              // or disabling the bot. Now: wipe the loadout + disable bot +
              // reset skip-levelup + unlock time before navigating out, so
              // the next regular run starts as a clean baseline.
              try { clearLoadout(); } catch { /* never block navigation */ }
              try { setLabFrozenTimeSec(null); } catch { /* */ }
              try { setLabSkipLevelUp(false); } catch { /* */ }
              try { if (isBotEnabled()) toggleBot(); } catch { /* */ }
              onBack();
            }}
            title="Back to menu (clears lab loadout, disables bot, resets lab settings)"
          >← MENU</button>
        </div>

        {/* BETA 17.76 — Dylan: "i need the game to run like normal as if im
            playing the game... a lot of settings in here, maybe a way to make
            sure enemies and such was exactly like it is when im playing."
            One-click reset that brings the lab to live-game-equivalent state:
            wave UNLOCKED, density 1×, retreat tuning at defaults, bot ON,
            level-up skip ON (so bot picks itself), camera 1×. */}
        <div style={sectionStyle}>
          <button
            style={{
              ...btn,
              width: '100%',
              padding: '10px 12px',
              fontSize: 13,
              fontWeight: 700,
              background: 'rgba(80,200,120,0.18)',
              border: '1px solid rgba(80,200,120,0.55)',
              color: '#7fff9f',
              letterSpacing: '0.06em',
            }}
            title="One click — wave unlocked, density 1×, retreat tuning reset, bot enabled, camera 1×. Game runs like a real run, bot drives random builds."
            onClick={() => {
              setLabFrozenTimeSec(null);    // unlock wave/time
              setWaveSpawnRateMult(1.0);    // normal spawn rate
              resetRetreatTuning();          // back to live-game retreat behavior
              setRetreatSimZoom(1.0);       // normal camera
              setRetreatSimShowFovBox(false); // no FOV overlay
              setLabSkipLevelUp(true);      // bot doesn't need card screens
              if (!isBotEnabled()) { toggleBot(); }
              setTick(t => t + 1);          // bump so panel reflects new state
            }}
          >
            🎮 NORMAL GAME MODE (bot drives)
          </button>
          <div style={{ fontSize: 10, color: '#6b7785', marginTop: 6 }}>
            One-click setup: wave unlocked, density 1×, retreat defaults, camera 1×,
            bot ON, level-up auto-skip. Game behaves exactly like a normal run while
            the bot tests random builds. Combine with 🎲 RANDOMIZE (MAX) to cycle
            build coverage.
          </div>
        </div>

        {/* BETA 14.05 — Lab options ─────────────────────────────────────── */}
        <div style={sectionStyle}>
          <div style={h2}>Lab Options</div>
          <label style={{ ...row, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isLabSkipLevelUp()}
              onChange={e => { setLabSkipLevelUp(e.target.checked); }}
            />
            <span style={{ color: '#cfd8e3' }}>Skip level-up screens</span>
          </label>
          <div style={{ fontSize: 10, color: '#6b7785', marginLeft: 24 }}>
            Player still levels (XP counts) but the choice screen is skipped
            so weapons can be tested without UI interruption.
          </div>
          {/* BETA 17.96 — VFX master toggle. Per Dylan: "can you give us a
              vfx toggle on and off?" Suppresses particle systems, item-effect
              bursts (rocket explosions, T-bag, poison clouds), AND screen
              overlays (Last Stand red flash, low-HP vignette). Game simulation
              keeps running so combat math + telemetry are unaffected. */}
          <label style={{ ...row, cursor: 'pointer', marginTop: 6 }}>
            <input
              type="checkbox"
              checked={isVfxEnabled()}
              onChange={e => { setVfxEnabled(e.target.checked); setTick(t => t + 1); }}
            />
            <span style={{ color: '#cfd8e3' }}>VFX enabled</span>
          </label>
          <div style={{ fontSize: 10, color: '#6b7785', marginLeft: 24 }}>
            Off = no particles, no rocket bursts, no Last Stand red flash, no
            low-HP vignette. Useful for STRESS testing where R08-style multi-stack
            VFX otherwise blanket the screen. Sim still runs.
          </div>
          <div style={{ fontSize: 10, color: '#6b7785', marginTop: 6 }}>
            Movement: WASD or arrow keys (BETA 14.05). Player is invincible.
          </div>
          {/* BETA 17.79 — Combat Test Bench + Sound Inspector are power-user
              dev tools (weapon-sprite calibration, sound asset audit).
              Collapsed by default per Dylan: "remove all the shit that isnt
              needed... build it how you can understand it." */}
          <details style={{ marginTop: 8 }}>
            <summary style={{ cursor: 'pointer', color: '#6b7785', fontSize: 11, padding: '4px 0' }}>
              ▸ DEV TOOLS (Combat Test Bench, Sound Inspector)
            </summary>
            <button
              style={{ ...btn, marginTop: 6, width: '100%' }}
              onClick={() => setStatsLabOpen(o => !o)}
              title="Combat Test Bench — per-weapon sprite calibration, stat sliders, DPS/accuracy/kills metrics. Replaces old Barrel Calibrator + Stats & VFX Lab."
            >
              🧪 COMBAT TEST BENCH {statsLabOpen ? '▶ HIDE' : ''}
            </button>
            <button
              style={{ ...btn, marginTop: 6, width: '100%' }}
              onClick={() => setSoundInspectorOpen(true)}
              title="Audit every sound (real WAV vs procedural Web Audio vs shared). Each ▶ plays the in-game EXACT sound."
            >
              🔊 SOUND INSPECTOR
            </button>
          </details>
          {/* BETA 14.06 — Barrel Calibrator launcher hidden in 14.81 (merged into Combat Test Bench)
              Keeping the modal mount below in case anything still launches it programmatically.
          <button
            style={{ ...btn, marginTop: 8, width: '100%' }}
            onClick={() => setCalibratorOpen(true)}
            title="Open the per-weapon barrel-tip calibrator tool"
          >
            🎯 BARREL CALIBRATOR
          </button>
          */}

          {/* BETA 14.09 — Wave / time lock. User: "lock a level i am on.
              not really timer based. just allows me to control what wave
              i am on". The lock clamps state.time so wave-system difficulty
              stays frozen at the chosen minute. */}
          <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px dashed rgba(80,200,255,0.2)' }}>
            <div style={{ fontSize: 11, color: '#7fdcff', marginBottom: 6 }}>WAVE / TIME LOCK</div>
            <div style={{ ...row, marginBottom: 4 }}>
              <span style={{ fontSize: 10, color: '#cfd8e3' }}>Lock at minute:</span>
              <input
                type="number"
                min={0}
                max={60}
                step={0.5}
                placeholder="off"
                value={getLabFrozenTimeSec() != null ? (getLabFrozenTimeSec()! / 60) : ''}
                onChange={e => {
                  const raw = e.target.value;
                  if (raw === '') { setLabFrozenTimeSec(null); return; }
                  const min = parseFloat(raw);
                  if (!Number.isFinite(min) || min < 0) { setLabFrozenTimeSec(null); return; }
                  setLabFrozenTimeSec(min * 60);
                }}
                style={{ width: 64, background: '#0a0d12', color: '#cfd8e3', border: '1px solid rgba(80,200,255,0.25)', padding: '2px 4px', fontFamily: 'inherit' }}
              />
              <button
                onClick={() => setLabFrozenTimeSec(null)}
                style={{ ...btn, padding: '2px 8px', fontSize: 10 }}
                title="Unlock — let time advance normally"
                disabled={getLabFrozenTimeSec() == null}
              >UNLOCK</button>
            </div>
            <div style={{ fontSize: 10, color: '#6b7785' }}>
              {getLabFrozenTimeSec() != null
                ? `Locked at ${(getLabFrozenTimeSec()! / 60).toFixed(1)} min — wave difficulty + spawns frozen.`
                : 'Empty = unlocked. Set a number to freeze state.time at that minute (e.g. 5 = minute-5 difficulty).'}
            </div>
          </div>
        </div>

        {/* Character */}
        <div style={sectionStyle}>
          <div style={h2}>Character</div>
          <select
            value={character.id}
            onChange={e => {
              const c = CHARACTERS.find(c => c.id === e.target.value);
              if (c) setCharacter(c);
            }}
            style={{ width: '100%', background: '#0a0d12', color: '#cfd8e3', border: '1px solid rgba(80,200,255,0.3)', padding: 4, fontFamily: 'inherit' }}
          >
            {CHARACTERS.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <div style={{ fontSize: 10, color: '#6b7785', marginTop: 4 }}>
            Switching character reloads the arena. Notes are kept.
          </div>
        </div>

        {/* BETA 14.15 — Build Rotation: randomizer + 34 named presets + multi-
            build session CSV. Each click finalizes the previous build into a
            row + applies the new loadout + resets weapon telemetry. */}
        <div style={sectionStyle}>
          <div style={h2}>Build Rotation ({getSessionRowCount().finalized} finalized{getSessionRowCount().inProgress ? ' + 1 in progress' : ''})</div>
          <div style={{ ...row, gap: 4 }}>
            <button
              style={{ ...btn, flex: 1 }}
              onClick={() => {
                const r = randomizeBuild();
                applyBuild({
                  buildName: r.buildName,
                  buildSource: 'randomizer',
                  weapons: r.weapons,
                  items: r.items,
                  shrines: r.shrines,
                  percs: r.percs,
                  percLevel: r.percLevel,
                });
              }}
              title="MAX-build (realistic 10-min run ceiling): 4 weapons + 12 items + 8 shrines + 12 percs at avg lvl 3 (= 36 perc-levels distributed). Round-robin prefer-unseen."
            >🎲 RANDOMIZE (MAX)</button>
            {/* BETA 17.84 — LUDICROUS button. Dylan: "ludicis buiilds which
                is like 5 of everything and see what each item is doing and
                if each item is working." Stress-tests every item/shrine/
                perc at once — not achievable in real gameplay. */}
            <button
              style={{ ...btn, flex: 1 }}
              onClick={() => {
                const r = randomizeLudicrousBuild();
                applyBuild({
                  buildName: r.buildName,
                  buildSource: 'randomizer',
                  weapons: r.weapons,
                  items: r.items,
                  shrines: r.shrines,
                  percs: r.percs,
                  percLevel: r.percLevel,
                  itemStacksOverride: r.itemStacksOverride,
                });
              }}
              title="LUDICROUS: 5 stacks of every item (capped at maxStacks per item) + 20 random shrine buffs + 4 random percs at lvl 15 + 4 random weapons. Stress-tests far above realistic-MAX but respects the 4-perc-slot cap."
            >💥 RANDOMIZE (LUDICROUS)</button>
          </div>
          <div style={{ marginTop: 6 }}>
            <select
              defaultValue=""
              onChange={e => {
                const id = e.target.value;
                if (!id) return;
                const preset = PRESET_BUILDS.find((p: PresetBuild) => p.id === id);
                if (!preset) return;
                applyBuild({
                  buildName: preset.name,
                  buildSource: 'preset',
                  weapons: preset.weapons,
                  items: preset.items,
                  shrines: preset.shrines,
                  percs: preset.percs,
                  percLevel: preset.percLevel,
                });
                e.target.value = '';
              }}
              style={{ width: '100%', background: '#0a0d12', color: '#cfd8e3', border: '1px solid rgba(80,200,255,0.3)', padding: 4, fontFamily: 'inherit', fontSize: 11 }}
            >
              <option value="">📦 PRESET BUILDS ▾</option>
              {(['character', 'archetype', 'scaling-test', 'feature-test', 'synergy'] as const).map(cat => (
                <optgroup
                  key={cat}
                  label={
                    cat === 'character' ? '— Character defaults —'
                    : cat === 'archetype' ? '— Build archetypes —'
                    : cat === 'scaling-test' ? '— Scaling-disconnect tests —'
                    : cat === 'feature-test' ? '— Feature smoke tests —'
                    : '— Synergy combos —'
                  }
                >
                  {PRESET_BUILDS.filter((p: PresetBuild) => p.category === cat).map((p: PresetBuild) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div style={{ ...row, gap: 4, marginTop: 6 }}>
            <button
              style={{ ...btn, flex: 1, fontSize: 11 }}
              onClick={() => downloadMultiBuildCsv()}
              title="Download all session rows as CSV (finalizes the in-progress build first)"
            >↓ MULTI-BUILD CSV</button>
            <button
              style={{ ...btn, fontSize: 11 }}
              onClick={() => clearLoadout()}
              title="Wipe weapons + items + shrines + percs (keeps session rows, random pool, and character HP/level/gold)"
            >🧹 CLEAR LOADOUT</button>
            <button
              style={{ ...btnRed, fontSize: 11 }}
              onClick={() => {
                if (!window.confirm('Clear session rows + reset random pool? Notes are kept.')) return;
                clearSession();
              }}
              title="Reset session rows + random round-robin"
            >🗑 CLEAR</button>
          </div>
          {/* BETA 17.79 — BULK TEST. Dylan: "we need to be able to test
              EVERYTHING and track EVERYTHING. so we can do bulk test and
              fine tune this shit." One click → automated cycle of N random
              max-builds, each running for the chosen game-time before
              auto-rotating to the next. Final CSV download triggers
              automatically when the loop completes. */}
          {/* BETA 17.94 — Live stress-test telemetry. Dylan: "we need to watch
              these stress test... how hard are the enemies. how much is an
              attck doig?" Polls window.__getStressTelemetry() every 250ms
              and shows player stats / enemy aggregates / combat rates / FPS
              in a fixed panel so the data is visible while the bot plays. */}
          <div style={{ marginTop: 10, padding: 8, border: '1px solid rgba(255,127,80,0.45)', borderRadius: 4, background: 'rgba(40,20,10,0.55)' }}>
            <div style={{ fontSize: 11, color: '#ffaa77', marginBottom: 6, fontWeight: 600 }}>🔥 STRESS TELEMETRY (live)</div>
            <StressTelemetryPanel />
          </div>
          <div style={{ marginTop: 10, padding: 8, border: '1px solid rgba(127,220,255,0.25)', borderRadius: 4, background: 'rgba(20,30,40,0.4)' }}>
            <div style={{ fontSize: 11, color: '#7fdcff', marginBottom: 6, fontWeight: 600 }}>📊 BULK TEST</div>
            <BulkTestControl />
            <div style={{ fontSize: 10, color: '#6b7785', marginTop: 6 }}>
              Auto-cycles N RANDOMIZE-MAX builds, each running for the chosen
              duration. <strong>Enemy difficulty is pinned to your chosen minute</strong> the
              whole run so every build fights identical opposition — fair
              head-to-head comparison. Final CSV downloads automatically. Use
              5× / 10× time scale (debug overlay) to compress real time.
            </div>
          </div>
          <div style={{ fontSize: 10, color: '#6b7785', marginTop: 4 }}>
            🎲 MAX-build = 4 weapons + 10 items + 15 shrine buffs + 4 percs at
            lvl 6 (round-robin, prefer-unseen — full coverage in ~7 clicks).
            📦 picks from {PRESET_BUILDS.length} named loadouts. Each click
            finalizes the previous row + clears weapons/items/shrines/percs +
            resets telemetry counters for clean per-build signal. ↓CSV exports
            session as 11-column CSV.
          </div>
        </div>

        {/* Weapons */}
        <div style={sectionStyle}>
          <div style={h2}>Weapons ({equippedWeaponIds.length}/4 equipped)</div>
          {allWeapons.map(w => {
            const equipped = equippedWeaponIds.includes(w.id);
            return (
              <label key={w.id} style={{ ...row, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={equipped}
                  onChange={e => setWeaponEquipped(w.id, e.target.checked)}
                />
                <span style={{ color: equipped ? '#7fdcff' : '#cfd8e3' }}>{w.name}</span>
                <span style={{ fontSize: 9, color: '#5a6470', marginLeft: 'auto' }}>{w.id}</span>
              </label>
            );
          })}
        </div>

        {/* Items */}
        <div style={sectionStyle}>
          <div style={h2}>Items ({equippedItems.length} equipped)</div>
          <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid rgba(80,200,255,0.15)', padding: 4 }}>
            {allItems.map(item => {
              const eq = equippedItems.find(e => e.id === item.id);
              const stacks = eq?.stacks ?? 0;
              return (
                <div key={item.id} style={row}>
                  <input
                    type="number"
                    min={0}
                    max={99}
                    value={stacks}
                    onChange={e => setItemStacks(item.id, parseInt(e.target.value, 10) || 0)}
                    style={{ width: 40, background: '#0a0d12', color: '#cfd8e3', border: '1px solid rgba(80,200,255,0.25)', padding: '2px 4px', fontFamily: 'inherit' }}
                  />
                  {/* BETA 14.09 — +1 click button per item. User: "allow me
                      to have a button that can choose plus one of each item.
                      I can keep clicking it up." */}
                  <button
                    onClick={() => setItemStacks(item.id, stacks + 1)}
                    style={{ ...btn, padding: '2px 8px', fontSize: 11, minWidth: 28 }}
                    title="Add one stack"
                  >+1</button>
                  <span style={{ color: stacks > 0 ? '#7fdcff' : '#cfd8e3', flex: 1 }}>{item.name}</span>
                  <span style={{ fontSize: 9, color: '#5a6470' }}>{item.rarity}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Ultra Items — BETA 20.48. The 7 mega-boss-chest ultras live in a
            SEPARATE subsystem (ultraItems.ts), so they need their own toggles.
            On → ultraChestPool.collect (one-time onPickup + per-frame onUpdate);
            Off → ultraChestPool.remove (stops the tick + runs onRemove cleanup,
            e.g. disband Followers, undo The Grind's stacked permanent damage). */}
        <div style={sectionStyle}>
          <div style={{ ...h2, color: '#ff9a3c', borderBottomColor: 'rgba(255,90,0,0.3)' }}>
            ⚠ Ultra Items ({equippedUltraIds.length}/{allUltras.length} active)
          </div>
          <div style={{ border: '1px solid rgba(255,90,0,0.3)', padding: 4 }}>
            {allUltras.map(u => {
              const on = equippedUltraIds.includes(u.id);
              return (
                <label
                  key={u.id}
                  style={{ ...row, alignItems: 'flex-start', cursor: 'pointer', marginBottom: 6 }}
                  title={u.description}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={e => { setUltraItemEquipped(u.id, e.target.checked); bumpUltraTick(t => t + 1); }}
                    style={{ marginTop: 2 }}
                  />
                  <span style={{ flex: 1 }}>
                    <span style={{ color: on ? '#ff9a3c' : '#cfd8e3', fontWeight: on ? 700 : 400 }}>{u.name}</span>
                    <span style={{ display: 'block', fontSize: 9, color: '#7a8490', lineHeight: 1.3 }}>{u.description}</span>
                  </span>
                </label>
              );
            })}
          </div>
          <div style={{ fontSize: 9, color: '#5a6470', marginTop: 4 }}>
            Grants the mega-boss-chest ultras directly — no boss needed. Start a lab run first so there's a live player.
          </div>
        </div>

        {/* BETA 14.09 — Perks section with +1 stacking */}
        <div style={sectionStyle}>
          <div style={h2}>Perks (click +1 to stack)</div>
          <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid rgba(80,200,255,0.15)', padding: 4 }}>
            {listAllPercsWithState().map(p => (
              <div key={p.id} style={row}>
                <span
                  style={{ width: 30, fontSize: 11, color: p.level > 0 ? '#7fdcff' : '#5a6470', textAlign: 'center' }}
                  title={`Level ${p.level} / ${p.maxLevel}`}
                >
                  {p.level}/{p.maxLevel}
                </span>
                <button
                  onClick={() => incrementPercLevel(p.id)}
                  style={{ ...btn, padding: '2px 8px', fontSize: 11, minWidth: 28 }}
                  title="Increment perk level by 1"
                  disabled={p.level >= p.maxLevel}
                >+1</button>
                <button
                  onClick={() => clearPercFromSlot(p.id)}
                  style={{ ...btnRed, padding: '2px 6px', fontSize: 10 }}
                  title="Clear this perk from its slot"
                  disabled={p.level === 0}
                >×</button>
                <span style={{ color: p.level > 0 ? '#7fdcff' : '#cfd8e3', flex: 1 }}>{p.name}</span>
                <span style={{ fontSize: 9, color: '#5a6470' }}>{p.id}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 10, color: '#6b7785', marginTop: 4 }}>
            +1 places the perc in the first available slot at level 1, or
            increments the existing slot. Slots are filled greedily — if all
            are full, the lowest-level slot gets overwritten (lab behavior).
          </div>
        </div>

        {/* Enemy spawn */}
        <div style={sectionStyle}>
          <div style={h2}>Spawn Enemies</div>
          <div style={row}>
            <select
              value={spawnEnemyId}
              onChange={e => setSpawnEnemyId(e.target.value)}
              style={{ flex: 1, background: '#0a0d12', color: '#cfd8e3', border: '1px solid rgba(80,200,255,0.3)', padding: 4, fontFamily: 'inherit' }}
            >
              {allEnemies.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
            <input
              type="number"
              min={1}
              max={200}
              value={spawnCount}
              onChange={e => setSpawnCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
              style={{ width: 60, background: '#0a0d12', color: '#cfd8e3', border: '1px solid rgba(80,200,255,0.3)', padding: 4, fontFamily: 'inherit' }}
            />
          </div>
          <div style={{ ...row, marginTop: 6 }}>
            <button style={btn} onClick={handleSpawn}>+ SPAWN</button>
            <button style={btnRed} onClick={() => clearAllEnemies()}>CLEAR ALL</button>
          </div>
          <div style={{ fontSize: 10, color: '#6b7785', marginTop: 6 }}>
            Alive: {Object.entries(aliveEnemies).map(([k, v]) => `${k}×${v}`).join(', ') || '(none)'}
          </div>
        </div>

        {/* Notes */}
        <div style={sectionStyle}>
          <div style={h2}>Notes ({noteHistory.length} saved)</div>
          <textarea
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            placeholder="Type observations… 'Spatula slash feels too short' / 'fireball trail clips through walls' / etc."
            rows={4}
            style={{
              width: '100%', boxSizing: 'border-box', background: '#0a0d12', color: '#e6f2ff',
              border: '1px solid rgba(80,200,255,0.3)', padding: 6, fontFamily: 'inherit',
              fontSize: 11, resize: 'vertical',
            }}
          />
          <div style={{ ...row, marginTop: 6 }}>
            <button style={btn} onClick={handleSaveNote} disabled={!noteText.trim()}>+ SAVE NOTE</button>
            <button style={btn} onClick={() => downloadLabCsv()}>↓ CSV</button>
            <button style={btnRed} onClick={handleClearNotes}>WIPE ALL</button>
          </div>
          <div style={{ fontSize: 10, color: '#6b7785', marginTop: 6 }}>
            Each saved note captures the active weapons, items (with stacks), and alive enemies at the moment you click SAVE — so the CSV ties every observation to the loadout that produced it.
          </div>
        </div>

        {/* Recent notes preview */}
        {noteHistory.length > 0 && (
          <div style={sectionStyle}>
            <div style={h2}>Recent (last 3)</div>
            {noteHistory.slice(-3).reverse().map((n, i) => (
              <div key={i} style={{ borderLeft: '2px solid rgba(80,200,255,0.4)', paddingLeft: 6, marginBottom: 6 }}>
                <div style={{ fontSize: 9, color: '#5a6470' }}>{new Date(n.timestamp).toLocaleTimeString()} · {n.character}</div>
                <div style={{ fontSize: 11, color: '#cfd8e3', whiteSpace: 'pre-wrap' }}>{n.notes}</div>
              </div>
            ))}
          </div>
        )}

        {/* BETA 17.79 — Retreat Simulation is power-user tuning. Collapsed
            by default per Dylan: "redo the UI so its easy and simple."
            Click to expand if you actually need to tune retreat AI. */}
        <details style={{ marginBottom: 14, marginTop: 8 }}>
          <summary style={{ cursor: 'pointer', color: '#6b7785', fontSize: 11, padding: '6px 0', borderTop: '1px dashed rgba(80,200,255,0.15)' }}>
            ▸ ADVANCED — Retreat Simulation Sliders (collapsed)
          </summary>
          <RetreatSimSection />
        </details>
      </div>

      {/* BETA 14.06 — Barrel Calibrator modal overlay (per-weapon barrel-tip
          marker, copies JSON for PS Major to convert to barrelLength). */}
      {calibratorOpen && <BarrelCalibrator onClose={() => setCalibratorOpen(false)} />}
      {/* BETA 14.75 — Stats & VFX Lab modal (weapons/items/percs inspector + live preview canvas). */}
      {statsLabOpen && <StatsAndVFXLab onClose={() => setStatsLabOpen(false)} />}
      {/* BETA 15.64 — Sound Inspector modal (audit every sound + play in-game-exact). */}
      {soundInspectorOpen && <SoundInspector onClose={() => setSoundInspectorOpen(false)} />}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// BETA 14.38 — Retreat Simulation Panel
// ─────────────────────────────────────────────────────────────────────────
// BETA 17.79 — BULK TEST CONTROL
// ─────────────────────────────────────────────────────────────────────────
// Dylan: "we need to be able to test EVERYTHING and track EVERYTHING. so
// we can do bulk test and fine tune this shit."
//
// One-click automated cycle: N RANDOMIZE-MAX builds, each runs for the
// chosen game-time, then auto-rotates to the next. When the loop ends,
// the multi-build CSV downloads automatically. Use 5× / 10× time scale
// (debug overlay) to compress real time.
//
// Stop button breaks the cycle early.

function BulkTestControl(): React.ReactElement {
  const [count, setCount] = useState(50);
  const [durationSec, setDurationSec] = useState(90); // 90 game-sec per build
  // BETA 17.81 — Difficulty lock for fair bulk-test comparison. Dylan:
  // "each test probably isnt as stacked as the other. like max. you know.
  // so each test needs to have a good balance level of the enemy levels."
  // BETA 17.82 — Default raised from 5 → 10 per Dylan: "max build should
  // be tested on 10's." Minute 10 is where endless-multiplier scaling
  // kicks in (~4× enemy HP) — only well-built loadouts survive. Weak max-
  // builds visibly fail here; strong ones thrive. Most useful spread.
  const [lockMinute, setLockMinute] = useState(10);
  // BETA 17.84 — Build-mode toggle. MAX = realistic 10-min ceiling (default).
  // LUDICROUS = all items / shrines / percs maxed (per-item verification).
  // Dylan: "both of these styles of build should be tested at 10 minute waves."
  const [mode, setMode] = useState<'max' | 'ludicrous' | 'stress'>('max');
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const stopRef = useRef(false);

  const runBulk = async () => {
    setRunning(true);
    stopRef.current = false;
    setProgress(0);
    // BETA 17.81 — capture prior time-lock so we can restore after the loop
    // (don't clobber the user's manual lock if they had one set).
    const prevLockSec = getLabFrozenTimeSec();
    // Pin enemy difficulty for the whole bulk run so every build fights the
    // SAME enemies. Without this, state.time accumulates across builds and
    // build #50 fights endless-scaled enemies vs build #1's minute-0 weaklings.
    setLabFrozenTimeSec(lockMinute * 60);
    for (let i = 0; i < count; i++) {
      if (stopRef.current) break;
      // BETA 17.84 — choose MAX or LUDICROUS per the mode toggle.
      // BETA 17.92 — added STRESS mode (47-hr-equivalent: 87 stacks + lvl-100 percs + ALL shrines).
      const r = mode === 'stress'
        ? randomizeStressBuild()
        : mode === 'ludicrous'
          ? randomizeLudicrousBuild()
          : randomizeBuild();
      applyBuild({
        buildName: r.buildName,
        buildSource: 'randomizer',
        weapons: r.weapons,
        items: r.items,
        shrines: r.shrines,
        percs: r.percs,
        percLevel: r.percLevel,
        itemStacksOverride: r.itemStacksOverride,
        forceStacks: r.forceStacks,
      });
      setProgress(i + 1);
      // Wait `durationSec` of real-time per build (lab can run at high time-scale
      // independently). Poll every 250ms so the Stop button is responsive.
      const start = Date.now();
      while ((Date.now() - start) < durationSec * 1000) {
        if (stopRef.current) break;
        await new Promise(r => setTimeout(r, 250));
      }
    }
    // Auto-download CSV at the end
    if (!stopRef.current) {
      downloadMultiBuildCsv();
    }
    // BETA 17.81 — restore the prior time-lock (or null if unlocked before).
    setLabFrozenTimeSec(prevLockSec);
    setRunning(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: 11, color: '#cfd8e3' }}>
        <span>Builds:</span>
        <input
          type="number"
          min={1}
          max={500}
          value={count}
          disabled={running}
          onChange={e => setCount(Math.max(1, Math.min(500, parseInt(e.target.value, 10) || 1)))}
          style={{ width: 56, background: '#0a0d12', color: '#cfd8e3', border: '1px solid rgba(80,200,255,0.25)', padding: '2px 4px', fontFamily: 'inherit' }}
        />
        <span>×</span>
        <input
          type="number"
          min={5}
          max={600}
          value={durationSec}
          disabled={running}
          onChange={e => setDurationSec(Math.max(5, Math.min(600, parseInt(e.target.value, 10) || 90)))}
          style={{ width: 64, background: '#0a0d12', color: '#cfd8e3', border: '1px solid rgba(80,200,255,0.25)', padding: '2px 4px', fontFamily: 'inherit' }}
        />
        <span>sec each</span>
      </div>
      {/* BETA 17.81 — Difficulty lock for fair bulk-test comparison. Every
          build fights enemies at the SAME minute-X difficulty. Without this,
          state.time accumulates across builds and the comparison is bogus. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: 11, color: '#cfd8e3' }}>
        <span>Enemy level: minute</span>
        <input
          type="number"
          min={0}
          max={60}
          step={0.5}
          value={lockMinute}
          disabled={running}
          onChange={e => setLockMinute(Math.max(0, Math.min(9999, parseFloat(e.target.value) || 0)))}
          style={{ width: 56, background: '#0a0d12', color: '#cfd8e3', border: '1px solid rgba(80,200,255,0.25)', padding: '2px 4px', fontFamily: 'inherit' }}
        />
        <span style={{ fontSize: 10, color: '#6b7785' }}>
          (10 = MAX-build pressure; 5 = mid-game)
        </span>
      </div>
      {/* BETA 17.84 — Build-mode toggle. MAX = realistic ceiling, LUDICROUS
          = all items / shrines / percs maxed (per-item verification). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: 11, color: '#cfd8e3' }}>
        <span>Build mode:</span>
        <button
          onClick={() => !running && setMode('max')}
          disabled={running}
          style={{
            padding: '2px 8px',
            fontSize: 10,
            background: mode === 'max' ? 'rgba(80,200,255,0.25)' : 'rgba(80,200,255,0.05)',
            border: '1px solid rgba(80,200,255,0.55)',
            color: mode === 'max' ? '#7fdcff' : '#6b7785',
            cursor: running ? 'default' : 'pointer',
            fontFamily: 'inherit',
          }}
        >🎲 MAX</button>
        <button
          onClick={() => !running && setMode('ludicrous')}
          disabled={running}
          style={{
            padding: '2px 8px',
            fontSize: 10,
            background: mode === 'ludicrous' ? 'rgba(255,127,80,0.25)' : 'rgba(255,127,80,0.05)',
            border: '1px solid rgba(255,127,80,0.55)',
            color: mode === 'ludicrous' ? '#ffaa77' : '#6b7785',
            cursor: running ? 'default' : 'pointer',
            fontFamily: 'inherit',
          }}
        >💥 LUDICROUS</button>
        <button
          onClick={() => !running && setMode('stress')}
          disabled={running}
          title="STRESS: 100 stacks per item LITERALLY (bypasses per-item maxStacks ceilings) + ALL active shrines + 4 percs at LEVEL 100. Pair with high minute-lock (e.g. 2820 = 47 hours) to simulate top-of-leaderboard endurance runs. NOT for balance data — for render perf + entity-count stress testing."
          style={{
            padding: '2px 8px',
            fontSize: 10,
            background: mode === 'stress' ? 'rgba(255,40,40,0.30)' : 'rgba(255,40,40,0.06)',
            border: '1px solid rgba(255,40,40,0.55)',
            color: mode === 'stress' ? '#ff7777' : '#6b7785',
            cursor: running ? 'default' : 'pointer',
            fontFamily: 'inherit',
          }}
        >🔥 STRESS</button>
        <span style={{ fontSize: 10, color: '#6b7785' }}>
          {mode === 'max' ? '(realistic 10-min ceiling)' : mode === 'ludicrous' ? '(5×items/lvl-15 percs/20 shrines)' : '(100× LITERAL stacks per item / lvl-100 percs / ALL shrines)'}
        </span>
      </div>
      {!running ? (
        <button
          onClick={runBulk}
          style={{
            width: '100%',
            padding: '8px 12px',
            fontSize: 12,
            fontWeight: 700,
            background: 'rgba(80,200,120,0.18)',
            border: '1px solid rgba(80,200,120,0.55)',
            color: '#7fff9f',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >▶ START BULK TEST ({count} {mode === 'stress' ? '🔥 STRESS' : mode === 'ludicrous' ? '💥 LUDICROUS' : 'MAX'} builds)</button>
      ) : (
        <div>
          <div style={{ fontSize: 11, color: '#ffaa55', marginBottom: 4 }}>
            Running build {progress} / {count}…
          </div>
          <div style={{ width: '100%', height: 6, background: '#1a1f25', borderRadius: 3, overflow: 'hidden', marginBottom: 4 }}>
            <div style={{ width: `${(progress / count) * 100}%`, height: '100%', background: '#7fdcff', transition: 'width 0.2s' }} />
          </div>
          <button
            onClick={() => { stopRef.current = true; }}
            style={{
              width: '100%',
              padding: '6px 12px',
              fontSize: 11,
              background: 'rgba(220,80,80,0.18)',
              border: '1px solid rgba(220,80,80,0.55)',
              color: '#ff9f9f',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >■ STOP</button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Live-tuning panel for the BETA 14.29-37 retreat-and-warp behavior. User:
// "give me all the sliders i would need to make adjustments... so its not
// guess work." Adjusts retreat threshold / probability / duration / warp
// interval / spawn margin / spawn-max-distance in real time. Spawn-N-in-arc
// helper to generate test populations. Auto-kite bot toggle so the user
// can sit back and watch retreats fire while tuning. Copy-data button
// dumps the current tuning + spawn config + recent retreatEvents as JSON.

function RetreatSimSection(): React.ReactElement {
  // Force re-render whenever a slider moves so we display live values
  const [, setTick] = useState(0);
  const bump = () => setTick(t => t + 1);

  // Spawn config (persisted in component state)
  const [spawnCount, setSpawnCount] = useState(50);
  const [spawnAngle, setSpawnAngle] = useState(180); // 180 = west of player (default)
  const [spawnArc, setSpawnArc] = useState(360);
  const [spawnDist, setSpawnDist] = useState(400);
  const [spawnEnemyDef, setSpawnEnemyDef] = useState<'zombie'>('zombie');   // 23.55 — timmy deleted (23.39 took caseoh)

  // Bot active state for toggle display
  const [botActive, setBotActive] = useState(isBotEnabled());
  useEffect(() => {
    const id = setInterval(() => setBotActive(isBotEnabled()), 500);
    return () => clearInterval(id);
  }, []);

  const tuning = getRetreatTuning();

  const sectionH2: React.CSSProperties = {
    fontSize: 11, color: '#7fdcff', letterSpacing: '0.10em', marginBottom: 8, fontWeight: 700,
  };
  const subH: React.CSSProperties = { fontSize: 10, color: '#aaa', marginTop: 8, marginBottom: 4 };
  const btnStyle: React.CSSProperties = {
    background: '#1a3140', border: '1px solid #3a6478', color: '#cfd8e3',
    padding: '6px 10px', fontSize: 11, cursor: 'pointer', borderRadius: 3,
  };
  const btnAccent: React.CSSProperties = { ...btnStyle, background: '#1f4a25', borderColor: '#3a8c3a', color: '#aaffaa' };
  const sliderRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 };
  const labelStyle: React.CSSProperties = { fontSize: 10, color: '#cfd8e3', minWidth: 110 };
  const valueStyle: React.CSSProperties = { fontSize: 10, color: '#7fdcff', minWidth: 38, textAlign: 'right' };
  const inputStyle: React.CSSProperties = {
    background: '#0a1820', border: '1px solid #2a5070', color: '#cfd8e3', padding: '2px 6px',
    fontSize: 11, width: 60, borderRadius: 2,
  };
  // BETA 14.39 — Inline explanation text style
  const hint: React.CSSProperties = {
    fontSize: 9, color: '#7a8290', fontStyle: 'italic',
    marginLeft: 110, marginBottom: 6, marginTop: -4, lineHeight: 1.3,
  };

  function handleSpawn(): void {
    spawnEnemiesInArc({
      count: spawnCount,
      centerAngleDeg: spawnAngle,
      arcSpanDeg: spawnArc,
      distanceFromPlayerWu: spawnDist,
      enemyDefinitionId: spawnEnemyDef,
    });
    bump();
  }

  function handleClearAll(): void {
    clearAllEnemies();
    bump();
  }

  function handleToggleBot(): void {
    toggleBot();
    setBotActive(isBotEnabled());
  }

  function handleResetDefaults(): void {
    resetRetreatTuning();
    bump();
  }

  function handleCopyData(): void {
    const data = {
      tool: 'WeaponLab Retreat Simulation',
      version: 'BETA 14.38',
      tuning: getRetreatTuning(),
      spawnConfig: {
        count: spawnCount,
        centerAngleDeg: spawnAngle,
        arcSpanDeg: spawnArc,
        distanceFromPlayerWu: spawnDist,
        enemyDefinitionId: spawnEnemyDef,
      },
      botActive: isBotEnabled(),
      recentRetreatEvents: getLocalRetreatEvents().slice(-20),
      retreatEventCount: getLocalRetreatEvents().length,
      timestamp: new Date().toISOString(),
    };
    navigator.clipboard.writeText(JSON.stringify(data, null, 2)).then(
      () => alert('Retreat sim data copied to clipboard'),
      () => alert('Copy failed; see console for fallback'),
    );
    console.log('[RETREAT SIM DATA]', data);
  }

  return (
    <div style={{ marginBottom: 14, paddingTop: 8, borderTop: '2px solid #3a6478' }}>
      <div style={sectionH2}>🎯 RETREAT SIMULATION</div>
      <div style={{ fontSize: 9, color: '#888', marginBottom: 8, lineHeight: 1.4 }}>
        Live-tune retreat/warp behavior. Toggle Auto-Kite Bot for hands-free observation. Use <b>Killa Peanut</b> for fast-kite testing.
      </div>

      {/* Bot + Clear row */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        <button
          style={botActive ? btnAccent : btnStyle}
          onClick={handleToggleBot}
          title="Toggle auto-kite bot so you can watch retreats fire"
        >
          🤖 {botActive ? 'BOT ON' : 'BOT OFF'}
        </button>
        <button style={btnStyle} onClick={handleClearAll} title="Kill all alive enemies">
          🧹 CLEAR
        </button>
        <button style={btnStyle} onClick={handleCopyData} title="Copy tuning + spawn + recent retreat events to clipboard">
          📋 COPY DATA
        </button>
      </div>

      {/* Spawn controls */}
      <div style={subH}>SPAWN ENEMIES</div>
      <div style={sliderRow}>
        <span style={labelStyle}>Count</span>
        <input type="number" min={1} max={200} value={spawnCount}
          onChange={e => setSpawnCount(Math.max(1, Math.min(200, +e.target.value)))} style={inputStyle} />
        <select value={spawnEnemyDef} onChange={e => setSpawnEnemyDef(e.target.value as 'zombie')}
          style={{ ...inputStyle, width: 80 }}>
          <option value="zombie">zombie</option>
        </select>
      </div>
      <div style={hint}>
        Number of enemies and which type to spawn at once.
      </div>
      <div style={sliderRow}>
        <span style={labelStyle}>Center angle°</span>
        <input type="range" min={0} max={360} step={5} value={spawnAngle}
          onChange={e => setSpawnAngle(+e.target.value)} style={{ flex: 1 }} />
        <span style={valueStyle}>{spawnAngle}°</span>
      </div>
      <div style={hint}>
        Compass direction the spawn arc is centered on. 0° = east of player, 90° = south, 180° = west, 270° = north.
      </div>
      <div style={sliderRow}>
        <span style={labelStyle}>Arc span°</span>
        <input type="range" min={10} max={360} step={5} value={spawnArc}
          onChange={e => setSpawnArc(+e.target.value)} style={{ flex: 1 }} />
        <span style={valueStyle}>{spawnArc}°</span>
      </div>
      <div style={hint}>
        How wide the spawn fan is. 360° = full ring around player. 60° = tight cluster from one direction.
      </div>
      <div style={sliderRow}>
        <span style={labelStyle}>Distance wu</span>
        <input type="range" min={50} max={1500} step={25} value={spawnDist}
          onChange={e => setSpawnDist(+e.target.value)} style={{ flex: 1 }} />
        <span style={valueStyle}>{spawnDist}</span>
      </div>
      <div style={hint}>
        How far from player the spawn ring sits. 50 = right next to player. 1000+ = far off-screen.
      </div>
      <button style={{ ...btnAccent, width: '100%', marginBottom: 10 }} onClick={handleSpawn}>
        🎯 SPAWN {spawnCount} {spawnEnemyDef.toUpperCase()}{spawnCount > 1 ? 'S' : ''}
      </button>

      {/* BETA 14.42 — Wave spawn rate multiplier (VS-style continuous density) */}
      <div style={subH}>WAVE SPAWN DENSITY</div>
      <div style={sliderRow}>
        <span style={labelStyle}>Rate ×</span>
        <input type="range" min={0.1} max={10} step={0.1} value={getWaveSpawnRateMult()}
          onChange={e => { setWaveSpawnRateMult(+e.target.value); bump(); }}
          style={{ flex: 1 }} />
        <span style={valueStyle}>{getWaveSpawnRateMult().toFixed(1)}×</span>
      </div>
      <div style={hint}>
        Multiplies wave-system spawn rate. 1× = default scaling. 5× = constant flood (VS-style late-game). 0.1× = trickle for slow tests.
      </div>

      {/* BETA 14.39 — Camera / FOV controls */}
      <div style={subH}>CAMERA / FOV</div>
      <div style={sliderRow}>
        <span style={labelStyle}>Zoom-out</span>
        <input type="range" min={0.25} max={1.0} step={0.05} value={getRetreatSimZoom()}
          onChange={e => { setRetreatSimZoom(+e.target.value); bump(); }}
          style={{ flex: 1 }} />
        <span style={valueStyle}>{getRetreatSimZoom().toFixed(2)}×</span>
      </div>
      <div style={hint}>
        Drag DOWN to see more of the map. 1.0× = normal zoom, 0.25× = max wide.
      </div>
      <label style={{ ...sliderRow, cursor: 'pointer' }}>
        <input type="checkbox" checked={getRetreatSimShowFovBox()}
          onChange={e => { setRetreatSimShowFovBox(e.target.checked); bump(); }} />
        <span style={{ fontSize: 11, color: '#cfd8e3' }}>Show normal FOV outline</span>
      </label>
      <div style={hint}>
        White dashed rectangle = what the player would see at normal zoom. Anything outside the box is "off-screen".
      </div>

      {/* Retreat tuning sliders */}
      <div style={subH}>RETREAT TUNING (live)</div>
      {/* BETA 14.41 — Attack-mode toggle (top of retreat tuning) */}
      <label style={{ ...sliderRow, cursor: 'pointer' }}>
        <input type="checkbox" checked={tuning.attackModeOnly}
          onChange={e => { setRetreatTuning({ attackModeOnly: e.target.checked }); bump(); }} />
        <span style={{ fontSize: 11, color: tuning.attackModeOnly ? '#ffaa55' : '#cfd8e3' }}>
          🗡 ATTACK MODE (no retreats)
        </span>
      </label>
      <div style={hint}>
        When ON, all enemies just chase + attack. No retreating, no warping. Use this if you want to test pure pressure.
      </div>
      <div style={sliderRow}>
        <span style={labelStyle}>Move-req wu/s</span>
        <input type="range" min={0} max={300} step={5} value={tuning.requiredPlayerVelMag}
          onChange={e => { setRetreatTuning({ requiredPlayerVelMag: +e.target.value }); bump(); }}
          style={{ flex: 1 }} />
        <span style={valueStyle}>{tuning.requiredPlayerVelMag}</span>
      </div>
      <div style={hint}>
        Player must be moving this fast (wu/s) for retreats to fire. 0 = retreats always fire (sim observation). 80 = only when player kites. Player base speed is ~200.
      </div>
      <div style={sliderRow}>
        <span style={labelStyle}>Threshold s</span>
        <input type="range" min={0.1} max={60} step={0.1} value={tuning.trailingThresholdSec}
          onChange={e => { setRetreatTuning({ trailingThresholdSec: +e.target.value }); bump(); }}
          style={{ flex: 1 }} />
        <span style={valueStyle}>{tuning.trailingThresholdSec.toFixed(1)}</span>
      </div>
      <div style={hint}>
        Seconds an enemy must be alive near you before it's eligible to retreat. Lower = retreats fire faster on fresh enemies. Higher (up to 60) = enemies stay engaged for ages before peeling.
      </div>
      <div style={sliderRow}>
        <span style={labelStyle}>Prob/sec</span>
        <input type="range" min={0.1} max={60} step={0.1} value={tuning.retreatProbPerSec}
          onChange={e => { setRetreatTuning({ retreatProbPerSec: +e.target.value }); bump(); }}
          style={{ flex: 1 }} />
        <span style={valueStyle}>{tuning.retreatProbPerSec.toFixed(1)}</span>
      </div>
      <div style={hint}>
        Once eligible, chance per second to actually peel off and retreat. 1.5 = ~75% retreat in first second. 60 = nearly instant retreat.
      </div>
      <div style={sliderRow}>
        <span style={labelStyle}>Duration s</span>
        <input type="range" min={0.2} max={60} step={0.1} value={tuning.retreatDurationSec}
          onChange={e => { setRetreatTuning({ retreatDurationSec: +e.target.value }); bump(); }}
          style={{ flex: 1 }} />
        <span style={valueStyle}>{tuning.retreatDurationSec.toFixed(1)}</span>
      </div>
      <div style={hint}>
        How long the retreating enemy runs AWAY from you before its retreat ends. Higher = goes further before warp. 60s = effectively flees forever.
      </div>
      <div style={sliderRow}>
        <span style={labelStyle}>Warp tick s</span>
        <input type="range" min={0.5} max={15} step={0.5} value={tuning.warpIntervalSec}
          onChange={e => { setRetreatTuning({ warpIntervalSec: +e.target.value }); bump(); }}
          style={{ flex: 1 }} />
        <span style={valueStyle}>{tuning.warpIntervalSec.toFixed(1)}</span>
      </div>
      <div style={hint}>
        How often the recycle-to-forward-arc warp fires. Lower = more frequent warps. Default 5s.
      </div>
      <div style={sliderRow}>
        <span style={labelStyle}>Spawn margin</span>
        <input type="range" min={10} max={400} step={10} value={tuning.spawnMarginWu}
          onChange={e => { setRetreatTuning({ spawnMarginWu: +e.target.value }); bump(); }}
          style={{ flex: 1 }} />
        <span style={valueStyle}>{tuning.spawnMarginWu}</span>
      </div>
      <div style={hint}>
        Min wu past viewport edge for warp/spawn. Lower = enemies appear closer to visible edge. 50 = just outside view.
      </div>
      <div style={sliderRow}>
        <span style={labelStyle}>Spawn max</span>
        <input type="range" min={50} max={600} step={10} value={tuning.spawnMaxDistWu}
          onChange={e => { setRetreatTuning({ spawnMaxDistWu: +e.target.value }); bump(); }}
          style={{ flex: 1 }} />
        <span style={valueStyle}>{tuning.spawnMaxDistWu}</span>
      </div>
      <div style={hint}>
        Max wu past viewport edge. Range between margin and max = where new enemies land. Higher = sometimes farther away.
      </div>
      {/* BETA 14.40 — Warp count + angle randomness */}
      <div style={sliderRow}>
        <span style={labelStyle}>Warp count</span>
        <input type="range" min={1} max={50} step={1} value={tuning.warpCountPerTick}
          onChange={e => { setRetreatTuning({ warpCountPerTick: +e.target.value }); bump(); }}
          style={{ flex: 1 }} />
        <span style={valueStyle}>{tuning.warpCountPerTick}</span>
      </div>
      <div style={hint}>
        How many enemies respawn at once each warp tick. Higher = bigger waves coming back at you. 50 = recycle the whole horde at once.
      </div>
      <div style={sliderRow}>
        <span style={labelStyle}>Forward bias</span>
        <input type="range" min={0} max={100} step={5} value={tuning.warpForwardBiasPct}
          onChange={e => { setRetreatTuning({ warpForwardBiasPct: +e.target.value }); bump(); }}
          style={{ flex: 1 }} />
        <span style={valueStyle}>{tuning.warpForwardBiasPct}%</span>
      </div>
      <div style={hint}>
        Where respawned enemies appear. 0% = pure random around player (chaos). 100% = always in front of player. 60% = mostly forward, some random.
      </div>
      <button style={{ ...btnStyle, width: '100%', marginBottom: 6, marginTop: 6 }} onClick={handleResetDefaults}>
        ↻ Reset Defaults
      </button>
      <div style={{ fontSize: 9, color: '#666', marginTop: 8, lineHeight: 1.4 }}>
        Retreats logged to CSV via retreatEventsLog col. Live count: <b>{getLocalRetreatEvents().length}</b>.
      </div>
    </div>
  );
}

// BETA 17.94 — Live stress-test telemetry panel. Polls window.__getStressTelemetry()
// every 250ms and renders a compact stats table. Dylan: "we need to watch these
// stress test... how hard are the enemies. how much is an attck doig? so on os
// forth." This is the panel that makes broken math visible (negative HP,
// underflow damage, stuck phases) in real time during the test.
function StressTelemetryPanel(): React.ReactElement {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  useEffect(() => {
    const id = setInterval(() => {
      try {
        const w = window as unknown as { __getStressTelemetry?: () => Record<string, unknown> | null };
        setData(w.__getStressTelemetry?.() ?? null);
      } catch { /* no-op */ }
    }, 250);
    return () => clearInterval(id);
  }, []);
  if (!data) return <div style={{ fontSize: 10, color: '#888' }}>(no game state — start a STRESS test to populate)</div>;
  const d = data as Record<string, number | string | null | undefined>;
  const cell = { padding: '2px 4px', fontSize: 10, color: '#cfd8e3' } as React.CSSProperties;
  const label = { ...cell, color: '#8aa', minWidth: 80 };
  const value = (v: unknown, danger = false) => ({ ...cell, color: danger ? '#ff5555' : '#fff', fontWeight: 600, textAlign: 'right' as const });
  // Detect dangerous broken values
  const hpBad = typeof d.hp === 'number' && (d.hp as number) < 0;
  const dmgBad = typeof d.dmgBonusPct === 'number' && (d.dmgBonusPct as number) < 0;
  const maxHpBad = typeof d.maxHp === 'number' && (d.maxHp as number) < 1;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto 1fr', gap: 0, fontFamily: '"Share Tech Mono", monospace' }}>
      <div style={label}>HP</div><div style={value(d.hp, hpBad)}>{String(d.hp)}/{String(d.maxHp)}</div>
      <div style={label}>HP %</div><div style={value(d.hpPct, hpBad)}>{d.hpPct != null ? `${d.hpPct}%` : '—'}</div>
      <div style={label}>Lvl</div><div style={value(d.level)}>{String(d.level)} ({String(d.xp)}/{String(d.xpToNext)})</div>
      <div style={label}>Phase</div><div style={value(d.phase)}>{String(d.phase ?? '—')}</div>
      <div style={label}>Speed</div><div style={value(d.speed)}>{String(d.speed)}</div>
      <div style={label}>Pickup</div><div style={value(d.pickupRange)}>{String(d.pickupRange)}</div>
      <div style={label}>DMG%</div><div style={value(d.dmgBonusPct, dmgBad)}>{String(d.dmgBonusPct)}%</div>
      <div style={label}>AS%</div><div style={value(d.atkSpeedPct)}>{String(d.atkSpeedPct)}%</div>
      <div style={label}>Crit%</div><div style={value(d.critChancePct)}>{String(d.critChancePct)}%</div>
      <div style={label}>CritDmg%</div><div style={value(d.critDmgPct)}>{String(d.critDmgPct)}%</div>
      <div style={label}>Luck</div><div style={value(d.luck)}>{String(d.luck)}</div>
      <div style={label}>Evasion</div><div style={value(d.evasion)}>{String(d.evasion)}%</div>
      <div style={label}>DR</div><div style={value(d.damageReduction)}>{String(d.damageReduction)}%</div>
      <div style={label}>Regen</div><div style={value(d.regenPerSec)}>{String(d.regenPerSec)}/s</div>
      <div style={label}>Lifesteal</div><div style={value(d.lifesteal)}>{String(d.lifesteal)}</div>
      <div style={label}>xpMult</div><div style={value(d.xpMult)}>{String(d.xpMult)}×</div>
      <div style={label}>goldMult</div><div style={value(d.goldMult)}>{String(d.goldMult)}×</div>
      <div style={label}>+Proj</div><div style={value(d.projCountBonus)}>{String(d.projCountBonus)}</div>
      <div style={label}>+Pierce</div><div style={value(d.pierceBonus)}>{String(d.pierceBonus)}</div>
      <div style={label}>+Bounce</div><div style={value(d.bounceBonus)}>{String(d.bounceBonus)}</div>
      {/* Separator */}
      <div style={{ gridColumn: '1 / 5', borderTop: '1px solid #444', margin: '4px 0' }} />
      <div style={label}>Enemies</div><div style={value(d.enemiesAlive)}>{String(d.enemiesAlive)} alive</div>
      <div style={label}>Proj/Part</div><div style={value(`${d.projectilesAlive ?? 0}/${d.particlesAlive ?? 0}`)}>{String(d.projectilesAlive)}/{String(d.particlesAlive)}</div>
      <div style={label}>Enemy HP</div><div style={value(d.enemyAvgHp)}>{String(d.enemyAvgHp)} avg ({String(d.enemyMaxHp)} max)</div>
      <div style={label}>Enemy DMG</div><div style={value(d.enemyAvgDmg)}>{String(d.enemyAvgDmg)} avg ({String(d.enemyMaxDmg)} max)</div>
      <div style={label}>Kills</div><div style={value(d.killCount)}>{String(d.killCount)}</div>
      <div style={label}>DMG taken</div><div style={value(d.damageTakenThisRun)}>{String(d.damageTakenThisRun)}</div>
      {/* Frame perf */}
      <div style={{ gridColumn: '1 / 5', borderTop: '1px solid #444', margin: '4px 0' }} />
      <div style={label}>Frame avg</div><div style={value(d.avgFrameTimeMs)}>{String(d.avgFrameTimeMs ?? '—')} ms</div>
      <div style={label}>Frame worst</div><div style={value(d.worstFrameTimeMs)}>{String(d.worstFrameTimeMs ?? '—')} ms</div>
      <div style={label}>Spikes</div><div style={value(d.frameSpikeCount)}>{String(d.frameSpikeCount)}</div>
      <div style={label}>State time</div><div style={value(d.stateTime)}>{String(d.stateTime)} s</div>
      {/* Warnings */}
      {(hpBad || dmgBad || maxHpBad) && (
        <div style={{ gridColumn: '1 / 5', marginTop: 4, padding: 4, background: 'rgba(255,40,40,0.12)', border: '1px solid rgba(255,40,40,0.4)', color: '#ff7777', fontSize: 10 }}>
          ⚠ Broken math detected: {hpBad ? 'HP<0 ' : ''}{maxHpBad ? 'maxHP<1 ' : ''}{dmgBad ? 'DMG%<0' : ''}— some item produces a negative bonus that breaks downstream.
        </div>
      )}
    </div>
  );
}
